import api from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface SupplierQuotationLineDTO {
  supplier_inquiry_line_id: string;
  unit_price: number;
  quoted_quantity: number;
  uom?: string;
  discount_percent?: number;
  lead_time_days?: number;
  promised_delivery_date?: string;
  remarks?: string;
  /** Supplier's offered specification / deviations from the requested spec */
  compliance_notes?: string | null;
  brand_offered?: string | null;
}

export interface CreateSupplierQuotationPayload {
  supplier_inquiry_supplier_id: string;
  supplier_quotation_ref?: string;
  quotation_date?: string;
  validity_date?: string;
  payment_terms?: string;
  delivery_terms?: string;
  remarks?: string;
  department_id?: string | null;
  lines: SupplierQuotationLineDTO[];
}

export interface UpdateSupplierQuotationPayload {
  department_id?: string | null;
  supplier_quotation_ref?: string;
  quotation_date?: string;
  validity_date?: string;
  payment_terms?: string;
  delivery_terms?: string;
  remarks?: string;
}

export interface SupplierQuotationLine {
  id: string;
  supplier_inquiry_line_id: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  quoted_quantity: number;
  uom?: string;
  unit_price: number;
  discount_percent?: number;
  line_total?: number;
  lead_time_days?: number;
  promised_delivery_date?: string;
  remarks?: string;
  compliance_notes?: string | null;
  brand_offered?: string | null;
}

export interface SupplierQuotation {
  id: string;
  company_id: string;
  supplier_inquiry_id: string;
  supplier_inquiry_supplier_id: string;
  supplier_id: string;
  department_id?: string | null;
  quotation_number?: string;
  supplier_quotation_ref?: string;
  quotation_date?: string;
  received_date?: string;
  validity_date?: string;
  payment_terms?: string;
  delivery_terms?: string;
  remarks?: string;
  status: string;
  revision_number?: number;
  created_at?: string;
  supplier?: { id: string; supplier_name: string; supplier_code?: string };
  lines?: SupplierQuotationLine[];
}

export class SupplierQuotationService {
  async list(params?: { supplier_inquiry_id?: string }): Promise<SupplierQuotation[]> {
    const q = new URLSearchParams();
    if (params?.supplier_inquiry_id) q.set('supplier_inquiry_id', params.supplier_inquiry_id);
    const res = await api.get(`/procurement/supplier-quotations?${q.toString()}`);
    const raw = unwrap<SupplierQuotation[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getByInquiry(inquiryId: string): Promise<SupplierQuotation[]> {
    const res = await api.get(`/procurement/supplier-quotations/by-inquiry/${inquiryId}`);
    const raw = unwrap<SupplierQuotation[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getById(id: string): Promise<SupplierQuotation> {
    const res = await api.get(`/procurement/supplier-quotations/${id}`);
    return unwrap<SupplierQuotation>(res);
  }

  async createManual(payload: CreateSupplierQuotationPayload): Promise<SupplierQuotation> {
    const res = await api.post('/procurement/supplier-quotations', payload);
    return unwrap<SupplierQuotation>(res);
  }

  async update(id: string, payload: UpdateSupplierQuotationPayload): Promise<SupplierQuotation> {
    const res = await api.put(`/procurement/supplier-quotations/${id}`, payload);
    return unwrap<SupplierQuotation>(res);
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/procurement/supplier-quotations/${id}`);
  }
}

export const supplierQuotationService = new SupplierQuotationService();
